import { Component, inject, Output, EventEmitter, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatMenuModule } from "@angular/material/menu";
import { MatDividerModule } from "@angular/material/divider";
import { RouterModule } from "@angular/router";
import { AuthService } from "../../services/auth.service";
import { Router } from "@angular/router";
import { Auth, User } from "@angular/fire/auth";
import { PermissionStateService } from "../../services/permission-state.service";
import { UserRole } from "../../models/permissions.model";
import { PermissionsService } from "../../services/permissions.service";

@Component({
	selector: "app-header",
	standalone: true,
	imports: [
		CommonModule,
		MatToolbarModule,
		MatIconModule,
		MatButtonModule,
		MatMenuModule,
		MatDividerModule,
		RouterModule,
	],
	templateUrl: "./header.component.html",
	styleUrls: ["./header.component.scss"],
})
export class HeaderComponent implements OnInit {
	private authService = inject(AuthService);
	private router = inject(Router);
	private permissionState = inject(PermissionStateService);
	private auth = inject(Auth);
	private permissionsService = inject(PermissionsService);

	hasGlobalAccess = false;
	hasOrganizationAccess = false;
	hasPartnerAccess = false;
	// Stricter than hasPartnerAccess: only partner_admin (or global) can read
	// the partners list per the rules. Used to gate the Partners nav link.
	hasPartnerAdminAccess = false;
	hasAssessmentAccess = false;
	hasIconsAccess = false;

	@Output() toggleSidebar = new EventEmitter<void>();

	user$ = this.authService.userDoc$;

	menuItems = [
		{ path: "/global-management", icon: "settings", label: "Global Management" },
		{ path: "/partners", icon: "business", label: "Partners" },
		{ path: "/organizations", icon: "groups", label: "Organizations" },
	];

	async ngOnInit() {
		const currentUser = this.auth.currentUser;
		if (!currentUser) return;

		const userId = currentUser.uid;

		// Check if user is from noldor.co.za domain for icons access
		this.hasIconsAccess = currentUser.email?.toLowerCase().endsWith("@noldor.co.za") ?? false;

		// Check permissions for different sections
		this.hasGlobalAccess = await this.permissionsService.isGlobalAdmin(userId);

		// Check organization access
		const orgPermissions = await this.permissionsService.getAllOrganizationAccess(userId);
		// Check partner access
		const partnerPermissions = await this.permissionsService.getAllPartnerAccess(userId);

		const isCoachInPartner = partnerPermissions.some((perm) => {
			// Check if the user has the coach role in any partner
			if (perm.data && perm.data.roles) {
				// Check both the roles map and the role field
				return perm.data.roles["coach"] === true || perm.role === UserRole.COACH;
			}
			return false;
		});

		// Coach access in orgs or partners
		const isCoachInOrg = orgPermissions.some((perm) => {
			// Check if the user has the coach role in any organization
			if (perm.data && perm.data.roles) {
				// Check both the roles map and the role field
				return perm.data.roles["coach"] === true || perm.role === UserRole.COACH;
			}
			return false;
		});

		const isPartnerAdminInPartner = partnerPermissions.some((perm) => {
			// Check if the user has the partner admin role in any partner
			if (perm.data && perm.data.roles) {
				// Check both the roles map and the role field
				return (
					perm.data.roles["partner_admin"] === true ||
					perm.role === UserRole.PARTNER_ADMIN
				);
			}
			return false;
		});

		this.hasOrganizationAccess =
			orgPermissions.length > 0 ||
			isCoachInOrg ||
			isCoachInPartner ||
			isPartnerAdminInPartner;
		this.hasPartnerAccess =
			partnerPermissions.length > 0 || isCoachInPartner || isPartnerAdminInPartner;
		this.hasPartnerAdminAccess = isPartnerAdminInPartner;

		// Check assessment access (Global admins, Partner admins, and coaches can access assessments)
		this.hasAssessmentAccess =
			this.hasGlobalAccess ||
			isCoachInPartner ||
			isPartnerAdminInPartner ||
			orgPermissions.some(
				(perm) =>
					perm.role === UserRole.COACH ||
					perm.role === UserRole.PARTNER_ADMIN ||
					(perm.roles &&
						Array.isArray(perm.roles) &&
						(perm.roles.includes(UserRole.COACH) ||
							perm.roles.includes(UserRole.PARTNER_ADMIN))) ||
					(perm.data &&
						perm.data.roles &&
						(perm.data.roles[UserRole.COACH] === true ||
							perm.data.roles[UserRole.PARTNER_ADMIN] === true)),
			);
	}

	onToggleSidebar() {
		this.toggleSidebar.emit();
	}

	async logout() {
		try {
			await this.authService.logout();
		} catch (error) {
			console.error("Logout failed:", error);
		}
	}

	async navigateTo(path: string) {
		// Check session validity before navigation
		const isValid = await this.authService.checkSessionBeforeNavigation();
		if (isValid) {
			this.router.navigate([path]);
		}
		// If invalid, the auth service will handle logout and show message
	}
}
